if (!customElements.get('knowledge-hub-section')) {
  class KnowledgeHubSection extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-hub-search-form]');
      this.input = this.querySelector('[data-hub-search-input]');
      this.resetButton = this.querySelector('[data-hub-search-reset]');
      this.status = this.querySelector('[data-hub-search-status]');
      this.panels = Array.from(this.querySelectorAll('[data-hub-panel]'));
      this.localPanels = Array.from(this.querySelectorAll('[data-hub-local-panel]'));
      this.blogPanel = this.querySelector('[data-hub-blog-panel]');
      this.blogCurated = this.querySelector('[data-hub-blog-curated]');
      this.searchView = this.querySelector('[data-hub-search-view]');
      this.searchResults = this.querySelector('[data-hub-search-results]');
      this.searchLoading = this.querySelector('[data-hub-search-loading]');
      this.searchError = this.querySelector('[data-hub-search-error]');
      this.searchFallback = this.querySelector('[data-hub-search-fallback]');
      this.tabsContainer = this.querySelector('[data-hub-tabs]');
      this.tabsScroller = this.querySelector('[data-hub-tabs-scroller]');
      this.localPanelMatches = new Map();
      this.blogSearchCount = 0;

      this.initTabScroller();
      this.initSearch();
    }

    disconnectedCallback() {
      if (this.tabsScroller && this.tabScrollHandler) {
        this.tabsScroller.removeEventListener('scroll', this.tabScrollHandler);
      }
      if (this.tabsScroller && this.tabAnchorInteractionHandler) {
        this.tabsScroller.removeEventListener('click', this.tabAnchorInteractionHandler);
        this.tabsScroller.removeEventListener('keydown', this.tabAnchorInteractionHandler);
      }

      if (this.tabResizeObserver) this.tabResizeObserver.disconnect();
      if (this.tabResizeHandler) window.removeEventListener('resize', this.tabResizeHandler);
      if (this.stickyStateHandler) {
        window.removeEventListener('scroll', this.stickyStateHandler);
        window.removeEventListener('resize', this.stickyStateHandler);
      }
      if (this.stickyStateFrame) window.cancelAnimationFrame(this.stickyStateFrame);

      if (this.form && this.submitHandler) this.form.removeEventListener('submit', this.submitHandler);
      if (this.form && this.resetHandler) this.form.removeEventListener('reset', this.resetHandler);
      if (this.input && this.inputHandler) this.input.removeEventListener('input', this.inputHandler);
      if (this.searchView && this.paginationHandler) {
        this.searchView.removeEventListener('click', this.paginationHandler);
      }
      if (this.popstateHandler) window.removeEventListener('popstate', this.popstateHandler);
      if (this.hashChangeHandler) window.removeEventListener('hashchange', this.hashChangeHandler);
      if (this.searchAnchorClickHandler) document.removeEventListener('click', this.searchAnchorClickHandler);

      window.clearTimeout(this.searchTimer);
      if (this.searchController) this.searchController.abort();
    }

    initTabScroller() {
      if (!this.tabsScroller) return;

      this.tabScrollHandler = this.updateTabScrollHint.bind(this);
      this.tabAnchorInteractionHandler = this.handleTabAnchorInteraction.bind(this);
      this.stickyStateHandler = this.requestStickyStateUpdate.bind(this);
      this.hashChangeHandler = () => this.handleLocationHash(true);
      this.tabsScroller.addEventListener('scroll', this.tabScrollHandler, { passive: true });
      this.tabsScroller.addEventListener('click', this.tabAnchorInteractionHandler);
      this.tabsScroller.addEventListener('keydown', this.tabAnchorInteractionHandler);
      window.addEventListener('hashchange', this.hashChangeHandler);
      window.addEventListener('scroll', this.stickyStateHandler, { passive: true });
      window.addEventListener('resize', this.stickyStateHandler);

      if ('ResizeObserver' in window) {
        this.tabResizeObserver = new ResizeObserver(this.tabScrollHandler);
        this.tabResizeObserver.observe(this.tabsScroller);
      } else {
        this.tabResizeHandler = this.tabScrollHandler;
        window.addEventListener('resize', this.tabResizeHandler);
      }

      requestAnimationFrame(this.tabScrollHandler);
      requestAnimationFrame(() => this.updateTabsStickyState());
      requestAnimationFrame(() => this.handleLocationHash(true));
    }

    handleTabAnchorInteraction(evt) {
      if (this.isActivatingAnchor) return;

      if (evt.type === 'click') {
        const tab = evt.target.closest('[role="tab"]');
        const shouldScroll = this.isTabBarStuck();
        if (tab) {
          requestAnimationFrame(() => {
            this.updateHashForTab(tab);
            if (shouldScroll) this.scrollToSelectedPanel(tab);
          });
        }
        return;
      }

      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(evt.key)) return;
      const shouldScroll = this.isTabBarStuck();
      requestAnimationFrame(() => {
        const activeTab = this.tabsScroller.querySelector('[role="tab"][aria-selected="true"]');
        if (activeTab) {
          this.updateHashForTab(activeTab);
          if (shouldScroll) this.scrollToSelectedPanel(activeTab);
        }
      });
    }

    requestStickyStateUpdate() {
      if (this.stickyStateFrame) return;

      this.stickyStateFrame = window.requestAnimationFrame(() => {
        this.stickyStateFrame = null;
        this.updateTabsStickyState();
      });
    }

    updateTabsStickyState() {
      if (!this.tabsContainer) return;

      const stickyTop = Number.parseFloat(window.getComputedStyle(this.tabsContainer).top) || 0;
      const tabsRect = this.tabsContainer.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();
      const isStuck = Math.abs(tabsRect.top - stickyTop) <= 2 && sectionRect.top < stickyTop;
      const wasStuck = this.tabsContainer.classList.contains('knowledge-hub__tabs--stuck');

      this.tabsContainer.classList.toggle('knowledge-hub__tabs--stuck', isStuck);
      if (wasStuck !== isStuck) {
        requestAnimationFrame(() => this.updateTabScrollHint());
      }
    }

    isTabBarStuck() {
      return this.tabsContainer?.classList.contains('knowledge-hub__tabs--stuck') || false;
    }

    scrollToSelectedPanel(tab) {
      if (!this.tabsContainer) return;

      const panelId = tab.getAttribute('aria-controls');
      const panel = this.panels.find((item) => item.id === panelId);
      if (!panel) return;

      const stickyTop = Number.parseFloat(window.getComputedStyle(this.tabsContainer).top) || 0;
      const scrollTop = window.scrollY + panel.getBoundingClientRect().top - stickyTop - this.tabsContainer.offsetHeight - 12;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      window.scrollTo({ top: Math.max(0, scrollTop), behavior });
    }

    activateTabFromHash(scrollToPanel = false) {
      if (!window.location.hash) return;

      let anchor = window.location.hash.slice(1);
      try {
        anchor = decodeURIComponent(anchor);
      } catch (error) {
        return;
      }

      const panel = this.panels.find((item) => item.id === anchor);
      if (!panel) return;

      const tab = this.querySelector(`[role="tab"][aria-controls="${anchor}"]`);
      if (!tab) return;

      this.isActivatingAnchor = true;
      tab.click();
      this.isActivatingAnchor = false;

      if (scrollToPanel) {
        requestAnimationFrame(() => panel.scrollIntoView({ behavior: 'auto', block: 'start' }));
      }
    }

    handleLocationHash(scrollToTarget = false) {
      if (this.focusSearchFromHash(scrollToTarget)) return;
      this.activateTabFromHash(scrollToTarget);
    }

    focusSearchFromHash(scrollToSearch = false) {
      if (!this.form?.id || !this.input || window.location.hash !== `#${this.form.id}`) return false;

      this.input.focus({ preventScroll: true });
      if (scrollToSearch) {
        requestAnimationFrame(() => {
          this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      return true;
    }

    handleSearchAnchorClick(evt) {
      if (!(evt.target instanceof Element) || !this.form?.id) return;

      const link = evt.target.closest('a[href]');
      if (!link || link.hash !== `#${this.form.id}`) return;

      const linkUrl = new URL(link.href, window.location.href);
      if (linkUrl.origin !== window.location.origin || linkUrl.pathname !== window.location.pathname) return;

      if (window.location.hash === linkUrl.hash) {
        requestAnimationFrame(() => this.focusSearchFromHash(true));
      }
    }

    updateHashForTab(tab) {
      const anchor = tab.getAttribute('aria-controls');
      if (!anchor || window.location.hash === `#${anchor}`) return;

      const url = new URL(window.location.href);
      url.hash = anchor;
      window.history.replaceState({}, '', url);
    }

    initSearch() {
      if (!this.form || !this.input || !this.blogPanel || !this.blogCurated || !this.searchView) return;

      this.submitHandler = this.handleSubmit.bind(this);
      this.inputHandler = this.handleInput.bind(this);
      this.resetHandler = this.handleReset.bind(this);
      this.paginationHandler = this.handlePagination.bind(this);
      this.popstateHandler = this.handlePopstate.bind(this);
      this.searchAnchorClickHandler = this.handleSearchAnchorClick.bind(this);

      this.form.addEventListener('submit', this.submitHandler);
      this.form.addEventListener('reset', this.resetHandler);
      this.input.addEventListener('input', this.inputHandler);
      this.searchView.addEventListener('click', this.paginationHandler);
      window.addEventListener('popstate', this.popstateHandler);
      document.addEventListener('click', this.searchAnchorClickHandler);

      const initialParams = new URL(window.location.href).searchParams;
      const initialQuery = initialParams.get('q')?.trim() || '';
      const initialPage = Number.parseInt(initialParams.get('page'), 10) || 1;

      if (initialQuery) {
        this.input.value = initialQuery;
        this.resetButton.hidden = false;
        this.filterLocalPanels(initialQuery);
        requestAnimationFrame(() => this.loadSearch(initialQuery, initialPage));
      }
    }

    handleSubmit(evt) {
      evt.preventDefault();
      window.clearTimeout(this.searchTimer);

      const query = this.input.value.trim();
      if (!query) {
        this.restoreCurated({ updateHistory: true });
        return;
      }

      this.filterLocalPanels(query);
      const currentQuery = new URL(window.location.href).searchParams.get('q') || '';
      const historyMode = currentQuery === query ? 'replace' : 'push';
      this.loadSearch(query, 1, { historyMode });
    }

    handleInput() {
      const query = this.input.value.trim();
      this.resetButton.hidden = !query;
      window.clearTimeout(this.searchTimer);
      if (this.searchController) {
        const activeController = this.searchController;
        this.searchController = null;
        activeController.abort();
      }

      if (!query) {
        this.restoreCurated({ updateHistory: true });
        return;
      }

      this.filterLocalPanels(query);
      this.showSearchView();
      this.searchResults.replaceChildren();
      this.searchError.hidden = true;
      this.searchLoading.hidden = false;
      this.searchView.setAttribute('aria-busy', 'true');
      this.status.textContent = `Searching all knowledge hub resources for “${query}”.`;
      this.setPanelCount(this.blogPanel, '…', true);

      this.searchTimer = window.setTimeout(() => {
        const hasSearchHistory = new URL(window.location.href).searchParams.has('q');
        this.loadSearch(query, 1, { historyMode: hasSearchHistory ? 'replace' : 'push' });
      }, 350);
    }

    handleReset() {
      window.clearTimeout(this.searchTimer);
      window.setTimeout(() => this.restoreCurated({ updateHistory: true }), 0);
    }

    handlePagination(evt) {
      const link = evt.target.closest('.pagination a[href]');
      if (!link || !this.searchView.contains(link)) return;

      evt.preventDefault();
      const url = new URL(link.href, window.location.origin);
      const page = Number.parseInt(url.searchParams.get('page'), 10) || 1;
      const query = this.input.value.trim();
      if (query) this.loadSearch(query, page, { historyMode: 'push', scrollToResults: true });
    }

    handlePopstate() {
      const params = new URL(window.location.href).searchParams;
      const query = params.get('q')?.trim() || '';
      const page = Number.parseInt(params.get('page'), 10) || 1;

      this.input.value = query;
      this.resetButton.hidden = !query;

      if (query) {
        this.filterLocalPanels(query);
        this.loadSearch(query, page);
      } else {
        this.restoreCurated();
      }
    }

    async loadSearch(query, page = 1, options = {}) {
      const { historyMode = null, scrollToResults = false } = options;
      if (!query) return;

      if (this.searchController) this.searchController.abort();
      this.searchController = new AbortController();
      const activeController = this.searchController;
      const requestUrl = this.buildSearchUrl(query, page, true);
      const fallbackUrl = this.buildSearchUrl(query, page, false);

      this.showSearchView();
      this.searchLoading.hidden = false;
      this.searchError.hidden = true;
      this.searchView.setAttribute('aria-busy', 'true');
      this.status.textContent = `Searching all knowledge hub resources for “${query}”.`;
      this.searchFallback.href = fallbackUrl;
      this.setPanelCount(this.blogPanel, '…', true);

      try {
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: activeController.signal
        });

        if (!response.ok) throw new Error(`Search request failed with ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const remoteResults = documentFragment.querySelector('[data-hub-remote-results]');
        if (!remoteResults) throw new Error('Search results markup was not returned');

        this.searchResults.innerHTML = remoteResults.outerHTML;
        this.blogSearchCount = Number.parseInt(remoteResults.dataset.totalResults, 10) || 0;
        this.setPanelCount(this.blogPanel, this.blogSearchCount, true);

        const localMatches = Array.from(this.localPanelMatches.values())
          .reduce((total, count) => total + count, 0);
        this.status.textContent = `${this.blogSearchCount} ${this.blogSearchCount === 1 ? 'article' : 'articles'} found across all blogs for “${query}”, with ${localMatches} related ${localMatches === 1 ? 'match' : 'matches'} in the other tabs.`;

        if (historyMode) this.updateBrowserUrl(query, page, historyMode);
        this.selectFirstMatchingTab();
        requestAnimationFrame(() => this.updateTabScrollHint());
        if (scrollToResults) this.searchView.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        if (error.name === 'AbortError') return;

        this.searchResults.replaceChildren();
        this.searchError.hidden = false;
        this.blogSearchCount = 0;
        this.setPanelCount(this.blogPanel, 0, true);
        const localMatches = Array.from(this.localPanelMatches.values())
          .reduce((total, count) => total + count, 0);
        this.status.textContent = `${localMatches} related ${localMatches === 1 ? 'resource was' : 'resources were'} found, but the article search could not be loaded.`;
        this.selectFirstMatchingTab();
        requestAnimationFrame(() => this.updateTabScrollHint());
      } finally {
        if (this.searchController === activeController) {
          this.searchLoading.hidden = true;
          this.searchView.removeAttribute('aria-busy');
        }
      }
    }

    filterLocalPanels(query) {
      const normalizedQuery = this.normalize(query);
      this.localPanelMatches.clear();

      this.localPanels.forEach((panel) => {
        const cards = Array.from(panel.querySelectorAll('[data-hub-card]'));
        let matches = 0;

        cards.forEach((card) => {
          const isMatch = this.matchesSearchText(card.dataset.searchText, normalizedQuery);
          card.hidden = !isMatch;
          if (isMatch) matches += 1;

          if (!isMatch) card.querySelector('details')?.removeAttribute('open');
        });

        this.localPanelMatches.set(panel, matches);
        this.setPanelCount(panel, matches, true);

        const emptyState = panel.querySelector('[data-hub-empty]');
        if (emptyState) {
          emptyState.hidden = matches > 0;
          emptyState.textContent = emptyState.dataset.searchText;
        }

        this.updateFaqCategories(panel, true);
      });

      requestAnimationFrame(() => this.updateTabScrollHint());
    }

    resetLocalPanels() {
      this.localPanels.forEach((panel) => {
        const cards = Array.from(panel.querySelectorAll('[data-hub-card]'));
        cards.forEach((card) => { card.hidden = false; });

        const emptyState = panel.querySelector('[data-hub-empty]');
        if (emptyState) {
          emptyState.hidden = cards.length > 0;
          emptyState.textContent = emptyState.dataset.defaultText;
        }

        this.updateFaqCategories(panel, false);
        this.setPanelCount(panel, '', false);
      });

      this.localPanelMatches.clear();
      this.setPanelCount(this.blogPanel, '', false);
    }

    updateFaqCategories(panel, isSearching) {
      const blocks = panel.querySelector('.faq__blocks');
      if (!blocks) return;

      const children = Array.from(blocks.children);
      if (!isSearching) {
        children
          .filter((child) => child.matches('[data-hub-faq-category]'))
          .forEach((category) => { category.hidden = false; });
        return;
      }

      let currentCategory = null;
      let categoryHasMatches = false;

      const updateCurrentCategory = () => {
        if (currentCategory) currentCategory.hidden = !categoryHasMatches;
      };

      children.forEach((child) => {
        if (child.matches('[data-hub-faq-category]')) {
          updateCurrentCategory();
          currentCategory = child;
          categoryHasMatches = false;
        } else if (child.matches('[data-hub-card]') && !child.hidden) {
          categoryHasMatches = true;
        }
      });

      updateCurrentCategory();
    }

    setPanelCount(panel, value, show) {
      if (!panel) return;

      const tab = this.querySelector(`[aria-controls="${panel.id}"]`);
      const count = tab?.querySelector('[data-hub-tab-count]');
      if (!count) return;

      count.textContent = value;
      count.hidden = !show;
    }

    selectFirstMatchingTab() {
      const activeTab = this.querySelector('[role="tab"][aria-selected="true"]');
      const activePanel = activeTab && document.getElementById(activeTab.getAttribute('aria-controls'));
      if (activePanel && this.getPanelMatchCount(activePanel) > 0) return;

      const matchingPanel = this.panels.find((panel) => this.getPanelMatchCount(panel) > 0);
      if (!matchingPanel) return;

      this.querySelector(`[aria-controls="${matchingPanel.id}"]`)?.click();
    }

    getPanelMatchCount(panel) {
      if (panel === this.blogPanel) return this.blogSearchCount;
      return this.localPanelMatches.get(panel) || 0;
    }

    normalize(value) {
      return (value || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    }

    matchesSearchText(value, normalizedQuery) {
      const normalizedValue = this.normalize(value);
      if (!normalizedQuery || normalizedValue.includes(normalizedQuery)) return true;

      const valueTerms = new Set(normalizedValue.split(' ').filter(Boolean));
      const queryTerms = [...new Set(normalizedQuery.split(' ').filter(Boolean))];
      const aliasGroups = [
        ['delivery', 'shipping', 'dispatch']
      ];

      return queryTerms.every((queryTerm) => {
        const aliases = aliasGroups.find((group) => group.includes(queryTerm)) || [queryTerm];
        return aliases.some((alias) => valueTerms.has(alias));
      });
    }

    buildSearchUrl(query, page, includeSection) {
      const url = new URL(this.form.action, window.location.origin);
      const formParams = new URLSearchParams(new FormData(this.form));
      formParams.set('q', query);
      if (page > 1) formParams.set('page', page);
      else formParams.delete('page');

      if (includeSection) formParams.set('section_id', this.form.dataset.searchSectionId);
      else formParams.delete('section_id');

      url.search = formParams.toString();
      return url.toString();
    }

    showSearchView() {
      this.classList.add('is-searching');
      this.blogCurated.hidden = true;
      this.searchView.hidden = false;
    }

    restoreCurated(options = {}) {
      const { updateHistory = false } = options;
      if (this.searchController) this.searchController.abort();

      this.classList.remove('is-searching');
      this.blogCurated.hidden = false;
      this.searchView.hidden = true;
      this.searchLoading.hidden = true;
      this.searchError.hidden = true;
      this.searchResults.replaceChildren();
      this.searchView.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.resetButton.hidden = !this.input.value.trim();
      this.blogSearchCount = 0;
      this.resetLocalPanels();

      if (updateHistory) {
        const historyMode = new URL(window.location.href).searchParams.has('q') ? 'push' : 'replace';
        this.updateBrowserUrl('', 1, historyMode);
      }
      requestAnimationFrame(() => this.updateTabScrollHint());
    }

    updateBrowserUrl(query, page, mode) {
      const url = new URL(window.location.href);

      if (query) {
        url.searchParams.set('q', query);
        if (page > 1) url.searchParams.set('page', page);
        else url.searchParams.delete('page');
      } else {
        url.searchParams.delete('q');
        url.searchParams.delete('page');
      }

      window.history[`${mode}State`]({}, '', url);
    }

    updateTabScrollHint() {
      if (!this.tabsScroller) return;

      const maxScrollLeft = this.tabsScroller.scrollWidth - this.tabsScroller.clientWidth;
      const hasScrollPrevious = maxScrollLeft > 1 && this.tabsScroller.scrollLeft > 1;
      const hasScrollNext = maxScrollLeft > 1 && this.tabsScroller.scrollLeft < maxScrollLeft - 1;

      this.tabsScroller.classList.toggle(
        'knowledge-hub__tabs-scroller--has-scroll-previous',
        hasScrollPrevious
      );
      this.tabsScroller.classList.toggle(
        'knowledge-hub__tabs-scroller--has-scroll-next',
        hasScrollNext
      );
    }
  }

  customElements.define('knowledge-hub-section', KnowledgeHubSection);
}
